import { useMemo } from "react";
import type { CyFunCategory } from "@/types/maturity";

type RadarChartProps = {
  categories: CyFunCategory[];
  scores: Record<string, number> | null;
  targetScore: number;
};

const SIZE = 400;
const CENTER = SIZE / 2;
const LEVELS = 5;
const OUTER_RADIUS = 160;

function polarToCartesian(
  angle: number,
  radius: number
): { x: number; y: number } {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: CENTER + radius * Math.cos(rad),
    y: CENTER + radius * Math.sin(rad),
  };
}

function RadarChart({ categories, scores, targetScore }: RadarChartProps) {
  const numAxes = categories.length;
  const angleStep = 360 / numAxes;

  const gridPolygons = useMemo(() => {
    return Array.from({ length: LEVELS }, (_, i) => {
      const level = i + 1;
      const r = (level / LEVELS) * OUTER_RADIUS;
      const points = categories
        .map((_, idx) => {
          const { x, y } = polarToCartesian(idx * angleStep, r);
          return `${x},${y}`;
        })
        .join(" ");
      return { level, points };
    });
  }, [categories, angleStep]);

  const targetPoints = useMemo(() => {
    const r = (targetScore / LEVELS) * OUTER_RADIUS;
    return categories
      .map((_, idx) => {
        const { x, y } = polarToCartesian(idx * angleStep, r);
        return `${x},${y}`;
      })
      .join(" ");
  }, [categories, targetScore, angleStep]);

  const scorePoints = useMemo(() => {
    if (!scores) return null;
    return categories
      .map((cat, idx) => {
        const score = scores[cat.code] ?? 0;
        const r = (score / LEVELS) * OUTER_RADIUS;
        const { x, y } = polarToCartesian(idx * angleStep, r);
        return `${x},${y}`;
      })
      .join(" ");
  }, [categories, scores, angleStep]);

  return (
    <div className="bg-slate-50/50 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-heading text-sm font-semibold text-slate-700">
          Diagrama Radar — CyFun® 2025
        </h3>
        <div className="flex items-center gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-5 h-0.5 border-t-2 border-dashed border-emerald-500" />
            <span>Țintă</span>
          </div>
          {scores && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-5 h-0.5 bg-amber-500" />
              <span>Scor actual</span>
            </div>
          )}
        </div>
      </div>
      <div className="w-full max-w-[400px] mx-auto">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="w-full">
          {gridPolygons.map(({ level, points }) => (
            <polygon
              key={level}
              points={points}
              fill="none"
              stroke="#E2E8F0"
              strokeWidth={level === 3 ? 1.5 : 0.5}
            />
          ))}

          {categories.map((_, idx) => {
            const { x, y } = polarToCartesian(idx * angleStep, OUTER_RADIUS);
            return (
              <line
                key={idx}
                x1={CENTER}
                y1={CENTER}
                x2={x}
                y2={y}
                stroke="#E2E8F0"
                strokeWidth={0.5}
              />
            );
          })}

          <polygon
            points={targetPoints}
            fill="rgba(16, 185, 129, 0.08)"
            stroke="#10B981"
            strokeWidth={2}
            strokeDasharray="6 3"
          />

          {scorePoints && (
            <polygon
              points={scorePoints}
              fill="rgba(245, 158, 11, 0.15)"
              stroke="#F59E0B"
              strokeWidth={2}
            />
          )}

          {categories.map((cat, idx) => {
            const { x, y } = polarToCartesian(
              idx * angleStep,
              OUTER_RADIUS + 20
            );
            return (
              <text
                key={cat.code}
                x={x}
                y={y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-[8px] fill-slate-500 font-medium"
              >
                {cat.code}
              </text>
            );
          })}

          {[1, 2, 3, 4, 5].map((level) => {
            const r = (level / LEVELS) * OUTER_RADIUS;
            const { x, y } = polarToCartesian(0, r);
            return (
              <text
                key={level}
                x={x + 8}
                y={y}
                className="text-[9px] fill-slate-400"
                dominantBaseline="middle"
              >
                {level}
              </text>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

export default RadarChart;
